/**
 * ManageTagsModal — admin console for the universal tagging system.
 *
 * Left pane: tag categories (create / edit / retire). Right pane: tags in the
 * selected category with usage counts (create / rename / recolor / retire /
 * delete / merge). Audit trail opens in the shared AuditLogModal.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import AuditLogModal from '../common/AuditLogModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { ListenButton } from '../common/AudioGuide';
import TagChip from '../common/tags/TagChip';
import TagUsageModal from '../common/tags/TagUsageModal';
import { contrastText, bulkCreateTags } from '../common/tags/tagApi';

const ACCENT = '#0e7490';

const SWATCHES = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#10b981', '#14b8a6',
  '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#6b7280', '#334155',
];

const emptyCategoryForm = {
  id: null, name: '', description: '', color: '#6b7280', scope: 'global',
  allowUserCreate: true, appliesTo: [],
};

const btn = (bg, fg = 'white') => ({
  padding: '6px 12px', backgroundColor: bg, color: fg, border: 'none',
  borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
});

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '6px 10px',
  border: '1px solid #dee2e6', borderRadius: 6, fontSize: '0.82rem', outline: 'none',
};

const ColorSwatches = ({ value, onChange, allowNone }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
    {allowNone && (
      <button
        onClick={() => onChange(null)}
        title="Inherit category color"
        style={{
          width: 22, height: 22, borderRadius: 6, cursor: 'pointer', fontSize: '0.6rem',
          border: value == null ? '2px solid #111827' : '1px solid #d1d5db',
          backgroundColor: 'white', color: '#6b7280',
        }}
      >
        —
      </button>
    )}
    {SWATCHES.map((c) => (
      <button
        key={c}
        onClick={() => onChange(c)}
        title={c}
        style={{
          width: 22, height: 22, borderRadius: 6, backgroundColor: c, cursor: 'pointer',
          border: value === c ? '2px solid #111827' : '1px solid transparent',
        }}
      />
    ))}
  </div>
);

const ManageTagsModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]); // from /tags/usage (all tags + counts)
  const [entityTypes, setEntityTypes] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [categoryForm, setCategoryForm] = useState(null); // null = closed; {...emptyCategoryForm}
  const [tagForm, setTagForm] = useState(null); // { id?, name, color }
  const [mergeSource, setMergeSource] = useState(null); // tag being merged
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usageTagId, setUsageTagId] = useState(null); // global tag explorer
  const [bulkImport, setBulkImport] = useState(null); // { text, projectId, result }
  const [projects, setProjects] = useState(null); // lazy-loaded for project-scoped import

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [catsRes, usageRes, typesRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/tags/categories?includeInactive=true'),
        authenticatedApiCall(instance, accounts, 'GET', '/tags/usage'),
        authenticatedApiCall(instance, accounts, 'GET', '/tags/entity-types'),
      ]);
      const cats = catsRes.data?.categories || [];
      setCategories(cats);
      setTags(usageRes.data?.tags || []);
      setEntityTypes(typesRes.data?.entityTypes || []);
      setSelectedCategoryId((prev) => prev ?? cats[0]?.id ?? null);
    } catch (e) {
      console.error('Failed to load tag admin data:', e);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) || null;
  const categoryTags = useMemo(
    () => tags.filter((t) => t.category_id === selectedCategoryId),
    [tags, selectedCategoryId]
  );

  const typesByModule = useMemo(() => {
    const map = new Map();
    for (const t of entityTypes) {
      if (!map.has(t.module)) map.set(t.module, []);
      map.get(t.module).push(t);
    }
    return [...map.entries()];
  }, [entityTypes]);

  const apiAction = async (fn, { reload = true } = {}) => {
    setSaving(true);
    setError('');
    try {
      await fn();
      if (reload) await load();
      return true;
    } catch (e) {
      console.error('Tag admin action failed:', e);
      setError(e.response?.data?.message || 'Action failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ----- category actions -----

  const saveCategory = async () => {
    const f = categoryForm;
    const payload = {
      name: f.name, description: f.description, color: f.color, scope: f.scope,
      allowUserCreate: f.allowUserCreate, appliesTo: f.appliesTo,
    };
    const ok = await apiAction(() => (
      f.id
        ? authenticatedApiCall(instance, accounts, 'PUT', `/tags/categories/${f.id}`, payload)
        : authenticatedApiCall(instance, accounts, 'POST', '/tags/categories', payload)
    ));
    if (ok) setCategoryForm(null);
  };

  const toggleCategoryActive = (cat) => apiAction(() =>
    authenticatedApiCall(instance, accounts, 'PUT', `/tags/categories/${cat.id}`, { isActive: !cat.is_active })
  );

  // ----- tag actions -----

  const saveTag = async () => {
    const f = tagForm;
    const ok = await apiAction(() => (
      f.id
        ? authenticatedApiCall(instance, accounts, 'PUT', `/tags/${f.id}`, { name: f.name, color: f.color })
        : authenticatedApiCall(instance, accounts, 'POST', '/tags', {
            categoryId: selectedCategoryId, name: f.name, color: f.color,
          })
    ));
    if (ok) setTagForm(null);
  };

  const toggleTagActive = (tag) => apiAction(() =>
    authenticatedApiCall(instance, accounts, 'PUT', `/tags/${tag.id}`, { isActive: !tag.is_active })
  );

  const deleteTag = (tag) => setConfirm({
    message: `Delete tag "${tag.name}"? This removes it from ${tag.usage_count || 0} record(s).`,
    onConfirm: () => apiAction(() => authenticatedApiCall(instance, accounts, 'DELETE', `/tags/${tag.id}`)),
  });

  const runMerge = async () => {
    if (!mergeSource || !mergeTargetId) return;
    const ok = await apiAction(() =>
      authenticatedApiCall(instance, accounts, 'POST', `/tags/${mergeSource.id}/merge`, {
        targetTagId: parseInt(mergeTargetId, 10),
      })
    );
    if (ok) { setMergeSource(null); setMergeTargetId(''); }
  };

  // ----- bulk import -----

  const openBulkImport = async () => {
    setBulkImport({ text: '', projectId: '', result: null });
    if (selectedCategory?.scope === 'project' && projects === null) {
      try {
        const r = await authenticatedApiCall(instance, accounts, 'GET', '/projects');
        setProjects(r.data?.projects || []);
      } catch (e) {
        console.error('Failed to load projects:', e);
        setProjects([]);
      }
    }
  };

  const runBulkImport = async () => {
    // Split on newlines, commas, and semicolons — pasted lists come in all shapes.
    const names = bulkImport.text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (names.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const result = await bulkCreateTags(instance, accounts, {
        categoryId: selectedCategoryId,
        names,
        projectId: selectedCategory?.scope === 'project' ? parseInt(bulkImport.projectId, 10) : undefined,
      });
      setBulkImport((prev) => ({ ...prev, result }));
      await load();
    } catch (e) {
      console.error('Bulk import failed:', e);
      setError(e.response?.data?.message || 'Bulk import failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const th = {
    padding: '6px 8px', fontSize: '0.66rem', color: '#6b7280', textTransform: 'uppercase',
    backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left',
  };
  const td = { padding: '6px 8px', fontSize: '0.8rem', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 12, width: '96%', maxWidth: 1480, height: '92vh', maxHeight: '94vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        {/* Header */}
        <div style={{ padding: '1rem 1.25rem', background: ACCENT, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px 12px 0 0' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>🏷️ Manage Tags</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setShowHelp(true)} style={{ ...btn('rgba(255,255,255,0.2)'), padding: '4px 10px' }} title="Help">❓ Help</button>
            <button onClick={() => setShowAuditLog(true)} style={{ ...btn('rgba(255,255,255,0.2)'), padding: '4px 10px' }}>📜 Audit Log</button>
            <button onClick={onClose} style={{ ...btn('rgba(255,255,255,0.2)'), padding: '4px 10px' }}>✕ Close</button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '8px 1.25rem', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '0.8rem', borderBottom: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* Categories pane */}
          <div style={{ width: 380, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>Categories</span>
              <button onClick={() => setCategoryForm({ ...emptyCategoryForm })} style={btn(ACCENT)}>＋ New</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: 16, color: '#94a3b8', fontSize: '0.8rem' }}>Loading…</div>
              ) : categories.map((cat) => (
                <div
                  key={cat.id}
                  onClick={() => setSelectedCategoryId(cat.id)}
                  style={{
                    padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #f8fafc',
                    backgroundColor: cat.id === selectedCategoryId ? '#ecfeff' : 'transparent',
                    borderLeft: cat.id === selectedCategoryId ? `3px solid ${ACCENT}` : '3px solid transparent',
                    opacity: cat.is_active ? 1 : 0.55,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 999, backgroundColor: cat.color, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1f2937' }}>{cat.name}</span>
                    {!cat.is_active && <span style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700 }}>RETIRED</span>}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 2, paddingLeft: 17 }}>
                    {cat.scope === 'project' ? 'Per-project' : 'Global'}
                    {' · '}{cat.tag_count} tag{cat.tag_count === 1 ? '' : 's'}
                    {' · '}{cat.allow_user_create ? 'users can add tags' : 'admin curated'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tags pane */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {selectedCategory ? (
              <>
                <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1f2937' }}>{selectedCategory.name}</span>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 8 }}>
                      {selectedCategory.description || (selectedCategory.scope === 'project' ? 'Project-scoped tags' : 'Global tags')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setCategoryForm({
                        id: selectedCategory.id,
                        name: selectedCategory.name,
                        description: selectedCategory.description || '',
                        color: selectedCategory.color,
                        scope: selectedCategory.scope,
                        allowUserCreate: selectedCategory.allow_user_create,
                        appliesTo: selectedCategory.applies_to || [],
                      })}
                      style={btn('#f1f5f9', '#334155')}
                    >
                      ✏️ Edit category
                    </button>
                    <button onClick={() => toggleCategoryActive(selectedCategory)} style={btn('#f1f5f9', '#334155')}>
                      {selectedCategory.is_active ? '🗄️ Retire' : '↩️ Reactivate'}
                    </button>
                    <button onClick={openBulkImport} style={btn('#f1f5f9', '#334155')} title="Paste a list (equipment tags, drawing numbers, ...) to create many tags at once">
                      📥 Bulk import
                    </button>
                    <button onClick={() => setTagForm({ name: '', color: null })} style={btn(ACCENT)}>＋ New tag</button>
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>Tag</th>
                        <th style={{ ...th, width: 140, whiteSpace: 'nowrap' }}>Used on</th>
                        <th style={{ ...th, width: 400, whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryTags.length === 0 ? (
                        <tr><td colSpan={3} style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>No tags in this category yet.</td></tr>
                      ) : categoryTags.map((tag) => (
                        <tr key={tag.id} style={{ opacity: tag.is_active ? 1 : 0.55 }}>
                          <td style={td}>
                            <TagChip tag={tag} size="md" />
                            {tag.project_job_number && (
                              <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginLeft: 6 }}>job {tag.project_job_number}</span>
                            )}
                            {!tag.is_active && <span style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, marginLeft: 6 }}>RETIRED</span>}
                          </td>
                          <td style={td}>
                            <span
                              onClick={() => tag.usage_count > 0 && setUsageTagId(tag.id)}
                              title={tag.usage_count > 0 ? 'See everything carrying this tag' : undefined}
                              style={{
                                cursor: tag.usage_count > 0 ? 'pointer' : 'default',
                                textDecoration: tag.usage_count > 0 ? 'underline dotted' : 'none',
                              }}
                            >
                              <span style={{ fontWeight: 700, color: tag.usage_count ? '#0f766e' : '#94a3b8' }}>{tag.usage_count}</span>
                              <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}> record{tag.usage_count === 1 ? '' : 's'}</span>
                              {tag.usage_count > 0 && <span style={{ fontSize: '0.72rem', marginLeft: 3 }}>🔎</span>}
                            </span>
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap' }}>
                              <button onClick={() => setTagForm({ id: tag.id, name: tag.name, color: tag.own_color ?? null })} style={btn('#f1f5f9', '#334155')}>Edit</button>
                              <button onClick={() => { setMergeSource(tag); setMergeTargetId(''); }} style={btn('#f1f5f9', '#334155')}>Merge…</button>
                              <button onClick={() => toggleTagActive(tag)} style={btn('#f1f5f9', '#334155')}>{tag.is_active ? 'Retire' : 'Reactivate'}</button>
                              <button onClick={() => deleteTag(tag)} style={btn('#fee2e2', '#b91c1c')}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ padding: 30, color: '#94a3b8', fontSize: '0.85rem' }}>Select a category on the left.</div>
            )}
          </div>
        </div>

        {/* Category form */}
        {categoryForm && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10010 }}>
            <div style={{ backgroundColor: 'white', borderRadius: 10, width: 460, maxHeight: '82vh', overflowY: 'auto', padding: '1.1rem 1.25rem', boxShadow: '0 15px 40px rgba(0,0,0,0.3)' }}>
              <h4 style={{ margin: '0 0 0.9rem 0', color: '#1f2937' }}>{categoryForm.id ? 'Edit Category' : 'New Category'}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>Name</label>
                  <input style={inputStyle} value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>Description</label>
                  <input style={inputStyle} value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>Default color</label>
                  <ColorSwatches value={categoryForm.color} onChange={(color) => setCategoryForm({ ...categoryForm, color })} />
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={{ fontSize: '0.8rem', color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <input type="radio" checked={categoryForm.scope === 'global'} onChange={() => setCategoryForm({ ...categoryForm, scope: 'global' })} />
                    Global (shared everywhere)
                  </label>
                  <label style={{ fontSize: '0.8rem', color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <input type="radio" checked={categoryForm.scope === 'project'} onChange={() => setCategoryForm({ ...categoryForm, scope: 'project' })} />
                    Per-project
                  </label>
                </div>
                <label style={{ fontSize: '0.8rem', color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={categoryForm.allowUserCreate}
                    onChange={(e) => setCategoryForm({ ...categoryForm, allowUserCreate: e.target.checked })}
                  />
                  Users can create tags in this category (uncheck = admin curated)
                </label>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    Applies to (leave all unchecked = everywhere)
                  </label>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', maxHeight: 150, overflowY: 'auto' }}>
                    {typesByModule.map(([module, types]) => (
                      <div key={module} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>{module}</div>
                        {types.map((t) => (
                          <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.76rem', color: '#334155', padding: '2px 0' }}>
                            <input
                              type="checkbox"
                              checked={categoryForm.appliesTo.includes(t.key)}
                              onChange={(e) => setCategoryForm({
                                ...categoryForm,
                                appliesTo: e.target.checked
                                  ? [...categoryForm.appliesTo, t.key]
                                  : categoryForm.appliesTo.filter((k) => k !== t.key),
                              })}
                            />
                            {t.label}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button onClick={() => setCategoryForm(null)} style={btn('#f1f5f9', '#334155')}>Cancel</button>
                <button onClick={saveCategory} disabled={saving || !categoryForm.name.trim()} style={btn(ACCENT)}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tag form */}
        {tagForm && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10010 }}>
            <div style={{ backgroundColor: 'white', borderRadius: 10, width: 400, padding: '1.1rem 1.25rem', boxShadow: '0 15px 40px rgba(0,0,0,0.3)' }}>
              <h4 style={{ margin: '0 0 0.9rem 0', color: '#1f2937' }}>
                {tagForm.id ? 'Edit Tag' : `New Tag in "${selectedCategory?.name}"`}
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>Name</label>
                  <input
                    style={inputStyle}
                    value={tagForm.name}
                    autoFocus
                    onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && tagForm.name.trim()) saveTag(); }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>Color (— inherits the category color)</label>
                  <ColorSwatches allowNone value={tagForm.color} onChange={(color) => setTagForm({ ...tagForm, color })} />
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  Preview:{' '}
                  <span style={{
                    backgroundColor: tagForm.color || selectedCategory?.color || '#6b7280',
                    color: contrastText(tagForm.color || selectedCategory?.color || '#6b7280'),
                    borderRadius: 999, padding: '2px 9px', fontSize: '0.72rem', fontWeight: 600,
                  }}>
                    {tagForm.name.trim() || 'tag name'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button onClick={() => setTagForm(null)} style={btn('#f1f5f9', '#334155')}>Cancel</button>
                <button onClick={saveTag} disabled={saving || !tagForm.name.trim()} style={btn(ACCENT)}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk import dialog */}
        {bulkImport && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10010 }}>
            <div style={{ backgroundColor: 'white', borderRadius: 10, width: 480, maxHeight: '84vh', overflowY: 'auto', padding: '1.1rem 1.25rem', boxShadow: '0 15px 40px rgba(0,0,0,0.3)' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#1f2937' }}>📥 Bulk import into "{selectedCategory?.name}"</h4>
              <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '0 0 0.7rem 0' }}>
                Paste one tag per line (commas work too) — equipment tags, drawing numbers, area codes, anything.
                Names that already exist are skipped, never duplicated.
              </p>
              {selectedCategory?.scope === 'project' && (
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>Project (this category is per-project)</label>
                  <select
                    value={bulkImport.projectId}
                    onChange={(e) => setBulkImport({ ...bulkImport, projectId: e.target.value })}
                    style={{ ...inputStyle, backgroundColor: 'white' }}
                  >
                    <option value="">Select project…</option>
                    {(projects || []).map((p) => (
                      <option key={p.id} value={p.id}>{p.job_number ? `${p.job_number} — ` : ''}{p.name}</option>
                    ))}
                  </select>
                  {projects === null && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 3 }}>Loading projects…</div>}
                </div>
              )}
              <textarea
                value={bulkImport.text}
                autoFocus
                onChange={(e) => setBulkImport({ ...bulkImport, text: e.target.value })}
                placeholder={'P-1001A\nTK-204\nAHU-01\n…'}
                style={{ ...inputStyle, height: 180, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.78rem' }}
              />
              {bulkImport.result && (
                <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#166534', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '6px 10px' }}>
                  Created {bulkImport.result.created} tag{bulkImport.result.created === 1 ? '' : 's'}
                  {bulkImport.result.skippedExisting > 0 && <> · skipped {bulkImport.result.skippedExisting} already existing</>}
                  {bulkImport.result.invalid?.length > 0 && <> · {bulkImport.result.invalid.length} too long (ignored)</>}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button onClick={() => setBulkImport(null)} style={btn('#f1f5f9', '#334155')}>
                  {bulkImport.result ? 'Done' : 'Cancel'}
                </button>
                <button
                  onClick={runBulkImport}
                  disabled={
                    saving
                    || !bulkImport.text.trim()
                    || (selectedCategory?.scope === 'project' && !bulkImport.projectId)
                  }
                  style={btn(ACCENT)}
                >
                  {saving ? 'Importing…' : 'Import'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Merge dialog */}
        {mergeSource && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10010 }}>
            <div style={{ backgroundColor: 'white', borderRadius: 10, width: 430, padding: '1.1rem 1.25rem', boxShadow: '0 15px 40px rgba(0,0,0,0.3)' }}>
              <h4 style={{ margin: '0 0 0.6rem 0', color: '#1f2937' }}>Merge "{mergeSource.name}"</h4>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 0.8rem 0' }}>
                All {mergeSource.usage_count} assignment(s) of <strong>{mergeSource.name}</strong> move to the tag you
                pick below, then <strong>{mergeSource.name}</strong> is deleted. Use this to clean up duplicates.
              </p>
              <select
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                style={{ ...inputStyle, backgroundColor: 'white' }}
              >
                <option value="">Select target tag…</option>
                {tags.filter((t) => t.id !== mergeSource.id && t.is_active).map((t) => (
                  <option key={t.id} value={t.id}>{t.category_name} / {t.name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button onClick={() => setMergeSource(null)} style={btn('#f1f5f9', '#334155')}>Cancel</button>
                <button onClick={runMerge} disabled={saving || !mergeTargetId} style={btn('#b45309')}>
                  {saving ? 'Merging…' : 'Merge & delete source'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help */}
        {showHelp && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10010 }}>
            <div style={{ backgroundColor: 'white', borderRadius: 10, width: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 15px 40px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '0.8rem 1.1rem', background: ACCENT, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '10px 10px 0 0' }}>
                <h4 style={{ margin: 0 }}>❓ Tags Help</h4>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <ListenButton compact guideId="manage-tags-help" title="Manage Tags Help" accent={ACCENT} dark />
                  <button onClick={() => setShowHelp(false)} style={{ ...btn('rgba(255,255,255,0.2)'), padding: '4px 10px' }}>✕</button>
                </div>
              </div>
              <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', fontSize: '0.85rem', color: '#334155', lineHeight: 1.55 }}>
                <p><strong>What tags are.</strong> Tags are colored labels you can stick on records anywhere in the portal — matrix lines, tracker rows, and more over time. They are for ad-hoc organization: flagging rework, marking items to review, grouping a punch list.</p>
                <p><strong>Categories</strong> keep the vocabulary tidy. Each category has a default color, a scope, and a rule about who can add tags to it:</p>
                <ul style={{ paddingLeft: 18, margin: '0.4rem 0' }}>
                  <li><strong>Global</strong> categories share one tag list across the whole portal.</li>
                  <li><strong>Per-project</strong> categories give every job its own tag list.</li>
                  <li><strong>Admin curated</strong> categories only accept tags created here; otherwise users can create tags on the fly while tagging.</li>
                  <li><strong>Applies to</strong> limits which record types offer the category (leave empty for everywhere).</li>
                </ul>
                <p><strong>Housekeeping.</strong> <em>Retire</em> hides a tag from pickers but keeps history. <em>Merge</em> moves every assignment onto another tag and deletes the duplicate. <em>Delete</em> removes the tag and all its assignments. Every change lands in the audit log.</p>
              </div>
            </div>
          </div>
        )}

        {confirm && (
          <ConfirmDialog
            isOpen
            title="Please confirm"
            message={confirm.message}
            confirmText="Delete"
            onConfirm={() => { confirm.onConfirm(); setConfirm(null); }}
            onCancel={() => setConfirm(null)}
          />
        )}

        <AuditLogModal
          isOpen={showAuditLog}
          onClose={() => setShowAuditLog(false)}
          endpoint="/tags/audit-log"
          title="Tag Audit Log"
          accentColor={ACCENT}
        />

        {/* Global tag explorer — everything carrying one tag */}
        {usageTagId != null && (
          <TagUsageModal tagId={usageTagId} onClose={() => setUsageTagId(null)} />
        )}
      </div>
    </div>
  );
};

export default ManageTagsModal;
